/**
 * Canvas-backed thumbnail generation for raster image files.
 *
 * Hoisted from the FileUploader capsule (SH-FILES-001) so both the
 * upload widget AND the homepage hero / anonymous artwork flow can
 * generate the same client-side thumbnail without depending on the
 * capsule module.
 *
 * SH-ARTWORK-001.
 */

/** MIME types `generateThumbnail` can render via `<canvas>`. */
export const RASTER_MIME_TYPES: ReadonlySet<string> = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
]);

/** Long-edge ceiling for the produced thumbnail. */
export const THUMBNAIL_MAX_PX = 128;

/**
 * Generate a Canvas-backed thumbnail data URL for a raster image
 * file. Returns `null` for non-raster files (the caller should fall
 * back to an icon). Resolves with `null` on decode errors so a
 * corrupt image still yields a graceful fallback rather than a
 * thrown rejection.
 *
 * SSR-safe: returns `null` when `document` or `URL` is absent.
 *
 * Produces a `data:image/jpeg;base64,...` URL at quality 0.7 — small
 * enough to live in Redux state without bloating store snapshots.
 */
export function generateThumbnail(file: File): Promise<string | null> {
  if (!RASTER_MIME_TYPES.has(file.type)) {
    return Promise.resolve(null);
  }
  if (typeof document === 'undefined' || typeof URL === 'undefined') {
    return Promise.resolve(null);
  }

  const objectUrl = URL.createObjectURL(file);

  return new Promise<string | null>((resolve) => {
    const img = new Image();

    const cleanup = (): void => {
      URL.revokeObjectURL(objectUrl);
    };

    img.onload = (): void => {
      try {
        const canvas = document.createElement('canvas');
        const ratio = Math.min(
          THUMBNAIL_MAX_PX / img.width,
          THUMBNAIL_MAX_PX / img.height,
          1,
        );
        canvas.width = Math.max(1, Math.round(img.width * ratio));
        canvas.height = Math.max(1, Math.round(img.height * ratio));
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        cleanup();
        resolve(dataUrl);
      } catch {
        cleanup();
        resolve(null);
      }
    };

    img.onerror = (): void => {
      cleanup();
      resolve(null);
    };

    img.src = objectUrl;
  });
}
